import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import { ICustomSeriesPaneRenderer, PaneRendererCustomData, PriceToCoordinateConverter, Time } from 'lightweight-charts'
import { VerticalLineData } from './data'
import { VertLineOptions } from './options'

interface VertLinePoint {
    x: number;
    sy: number;
    ey: number;
    color: string
    opacity: number,
    width: number
}

export class VertLinesRenderer<TData extends VerticalLineData>
    implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<Time, TData> | null = null
    _options: VertLineOptions | null = null

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<Time, TData>,
        options: VertLineOptions
    ): void {
        this._data = data
        this._options = options
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }
        const options = this._options
        const bars: VertLinePoint[] = this._data.bars.map(bar => {
            const sy = Number.isFinite(bar.originalData.sy) ? priceToCoordinate(bar.originalData.sy)! * renderingScope.verticalPixelRatio : Number.isNaN(bar.originalData.ey) ? 0 : (bar.originalData.sy > 0) ? 0 : renderingScope.bitmapSize.height - 1
            const ey = Number.isFinite(bar.originalData.ey) ? priceToCoordinate(bar.originalData.ey)! * renderingScope.verticalPixelRatio : Number.isNaN(bar.originalData.ey) ? renderingScope.bitmapSize.height - 1 : (bar.originalData.ey > 0) ? 0 : renderingScope.bitmapSize.height - 1
            return {
                x: bar.x * renderingScope.horizontalPixelRatio,
                sy: sy,
                ey: ey,
                opacity: bar.originalData.opacity,
                width: bar.originalData.width,
                color: (bar.originalData.customValues?.['color'] ?? '') as string
            }
        })

        bars.forEach((bar) => {
            if (!bar.x === undefined ||
                bar.sy === undefined ||
                bar.ey === undefined) {
                return
            }
            const x = bar.x
            const sy = bar.sy
            const ey = bar.ey
            const ctx = renderingScope.context
            ctx.beginPath()

            let lineDash: number[] = []
            const lineWidth = (bar.width ?? options.width) * window.devicePixelRatio
            if (options.lineType === 'dashed') {
                lineDash = [8, 3]
            } else if (options.lineType === 'dotted') {
                lineDash = [lineWidth, lineWidth]
            }
            ctx.setLineDash(lineDash)
            ctx.lineWidth = lineWidth
            ctx.strokeStyle = bar.color ?? options.color
            ctx.globalAlpha = (bar.opacity ?? options.opacity)

            ctx.moveTo(x, sy)
            ctx.lineTo(x, ey)
            ctx.stroke()
            ctx.closePath()
        })


    }
}
